/**
 * Reusable user-journey drawer. Given a user ({ userId | id, email, tenantId,
 * and optional sessions/events counts }), loads that user's event timeline from
 * the analytics API and renders it as a human-readable story, grouped into
 * sessions. A "Play" mode steps through the journey one action at a time — a
 * lightweight replay of what the user actually did (not a pixel/screen recording;
 * see the note in JourneyDrawer usage for the difference).
 *
 * Shared by the Product Analytics console, the tenant page's user table, and the
 * Platform Console Manage-users table.
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Typography, CircularProgress, Drawer, IconButton, Tooltip, Chip, LinearProgress } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LoginIcon from "@mui/icons-material/Login";
import RefreshIcon from "@mui/icons-material/Refresh";
import AddBusinessIcon from "@mui/icons-material/AddBusiness";
import ApartmentIcon from "@mui/icons-material/Apartment";
import DashboardIcon from "@mui/icons-material/SpaceDashboard";
import SettingsIcon from "@mui/icons-material/Settings";
import InsightsIcon from "@mui/icons-material/Insights";
import DescriptionIcon from "@mui/icons-material/Description";
import TouchAppIcon from "@mui/icons-material/TouchApp";
import ToggleOnIcon from "@mui/icons-material/ToggleOn";
import LinkIcon from "@mui/icons-material/Link";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { getAnalyticsUserJourney } from "../api/tenantApi";

const INK = "#0B2A4A", BLUE = "#1E6FE8", GREEN = "#12A150", MUTED = "#64748b", LINE = "#e3ebf3";

const fmt = (n) => (n == null ? "0" : Number(n).toLocaleString());
const shortId = (id) => (id ? String(id).slice(0, 8) : "—");
const prettyEvent = (e) => String(e || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
const fmtClock = (ms) => (ms ? new Date(ms).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" }) : "");
const fmtDayTime = (ms) => (ms ? new Date(ms).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");
const eventTime = (ev) => Number(ev?.clientTs) || Number(ev?.ts) || 0;
const fmtDuration = (ms) => {
  const totalSeconds = Math.max(0, Math.round(Number(ms || 0) / 1000));
  if (totalSeconds < 60) return `${totalSeconds}s`;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes < 60) return seconds ? `${minutes}m ${seconds}s` : `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  return remainder ? `${hours}h ${remainder}m` : `${hours}h`;
};

export const selectVisibleSessions = (sessions, limit = 10) => {
  const newest = sessions.slice(0, limit);
  const included = new Set(newest.map((session) => session.sid));
  const milestones = sessions.filter((session) =>
    !included.has(session.sid) && session.chrono.some((event) => {
      const name = String(event.event || "");
      return name.startsWith("onboarding_") || name.startsWith("portfolio_risk_");
    })
  );
  return newest.concat(milestones).sort((a, b) => b.end - a.end);
};

const eventSignature = (event) => {
  const props = event?.props || {};
  return [
    event?.event,
    props.kind,
    props.label,
    props.page,
    props.dialog,
    props.dialogPart,
    props.dialogSection,
    props.column,
    props.row,
    event?.path,
  ].map((value) => String(value || "")).join("|");
};

// Consecutive equivalent events are collapsed when they happen close together.
// The raw events and their chronological indexes remain available for playback.
export const groupRepeatedEvents = (events, maxGapMs = 5000) => {
  const groups = [];
  (events || []).forEach((event, index) => {
    const time = eventTime(event);
    const signature = eventSignature(event);
    const previous = groups[groups.length - 1];
    if (previous && previous.signature === signature && time - previous.end <= maxGapMs) {
      previous.events.push(event);
      previous.indexes.push(index);
      previous.count += 1;
      previous.end = time;
      previous.event = event;
      return;
    }
    groups.push({ signature, event, events: [event], indexes: [index], count: 1, start: time, end: time });
  });
  return groups;
};

export const isFrustrationGroup = (group) =>
  group?.event?.event === "click" && group.count >= 3 && group.end - group.start <= 5000;

const sessionPages = (events) => new Set((events || []).map((event) => event?.props?.page || pageName(event?.path)).filter(Boolean));

// Friendly name for an app path.
const pageName = (path) => {
  const p = String(path || "");
  if (!p || p === "/") return "the Dashboard";
  if (p.includes("/portfolio-risk")) return "Portfolio Risk Intelligence";
  if (p.startsWith("/company/") && p.includes("/analysis")) return "a company's analysis";
  if (p.startsWith("/company/") && p.includes("/custom")) return "a company's custom sources";
  if (p.startsWith("/company/")) return "a company page";
  if (p === "/company") return "the Companies view";
  if (p.startsWith("/settings")) return "Settings";
  if (p.startsWith("/analytics")) return "Product Analytics";
  if (p.startsWith("/platform/distributor")) return "a distributor page";
  if (p.startsWith("/distributor")) return "the Distributor Dashboard";
  if (p.startsWith("/platform/tenant")) return "a tenant page";
  if (p.startsWith("/platform")) return "the Platform Console";
  if (p.startsWith("/reporting")) return "BI & Reporting";
  if (p.startsWith("/api")) return "API & Docs";
  if (p.startsWith("/billing")) return "Billing & Plans";
  if (p.startsWith("/questions")) return "Questions";
  if (p.startsWith("/requeststatus")) return "Request Status";
  if (p.startsWith("/filteredSummary")) return "a filtered summary";
  if (p.startsWith("/uploadcompanyfiles")) return "the file upload";
  return p;
};

// Turn a raw event into { title, detail, kind } for display.
export const describeJourneyEvent = (ev) => {
  const props = ev.props || {};
  const path = props.path || ev.path || "";
  switch (ev.event) {
    case "session_start":
      return { title: "Signed in", detail: `Landed on ${pageName(props.entryPath || path)}`, kind: "login" };
    case "add_company":
      return { title: `Added ${props.companyName || "a company"}`, detail: "New counterparty", kind: "add" };
    case "company_refresh":
      return { title: `Refreshed ${props.companyName || "a company"}`, detail: "Re-ran the assessment", kind: "refresh" };
    case "company_view":
      return { title: `Opened ${props.companyName || "a company"}`, detail: "Viewed the counterparty", kind: "company" };
    case "page_view":
      return { title: `Viewed ${pageName(path)}`, detail: null, kind: pageKind(path) };
    case "bi_announcement_shown":
      return { title: "Saw the BI & Reporting announcement", detail: "New-feature popup shown", kind: "page" };
    case "bi_announcement_cta":
      return { title: "Explored BI & Reporting", detail: "From the announcement popup", kind: "click" };
    case "bi_announcement_dismissed":
      return { title: "Dismissed the BI & Reporting announcement", detail: "Closed the popup", kind: "click" };
    case "portfolio_risk_intro_shown":
      return { title: "Saw the Portfolio Risk introduction", detail: "One-time feature guide shown", kind: "insights" };
    case "portfolio_risk_intro_started":
      return { title: "Started exploring Portfolio Risk", detail: "Continued from the feature guide", kind: "insights" };
    case "portfolio_risk_intro_dismissed":
      return { title: "Closed the Portfolio Risk introduction", detail: "Dismissed the feature guide", kind: "click" };
    case "portfolio_risk_opened":
      return { title: "Opened Portfolio Risk Intelligence", detail: `Portfolio basis: ${props.basis === "ar" ? "Accounts receivable" : "Prospects"}`, kind: "insights" };
    case "portfolio_risk_basis_selected":
      return { title: `Selected ${props.basis === "ar" ? "Accounts receivable" : "Prospects"}`, detail: "Changed the Portfolio Risk basis", kind: "insights" };
    case "portfolio_risk_section_viewed":
      return { title: `Opened ${prettyEvent(props.section || "Portfolio Risk section")}`, detail: "Portfolio Risk Intelligence", kind: "insights" };
    case "portfolio_risk_ai_opened":
      return { title: "Opened Portfolio Risk AI", detail: `Portfolio basis: ${props.basis === "ar" ? "Accounts receivable" : "Prospects"}`, kind: "insights" };
    case "portfolio_risk_ai_question_submitted":
      return { title: "Asked Portfolio Risk AI", detail: "Submitted a portfolio question", kind: "insights" };
    case "portfolio_risk_ai_answered":
      return { title: "Received a Portfolio Risk AI answer", detail: "Advisory explanation completed", kind: "insights" };
    case "portfolio_risk_ai_failed":
      return { title: "Portfolio Risk AI request failed", detail: "The user remained in the assistant to retry", kind: "insights" };
    case "portfolio_risk_stress_run":
      return { title: "Ran a Portfolio Risk crisis test", detail: `Portfolio basis: ${props.basis === "ar" ? "Accounts receivable" : "Prospects"}`, kind: "insights" };
    case "portfolio_risk_stress_completed":
      return { title: "Completed a Portfolio Risk crisis test", detail: "Crisis results were displayed", kind: "insights" };
    case "portfolio_risk_stress_failed":
      return { title: "Portfolio Risk crisis test failed", detail: "The user remained on the scenario to retry", kind: "insights" };
    case "click": {
      const verb = props.kind === "toggle" ? "Toggled"
        : ["menu", "option", "select"].includes(props.kind) ? "Chose"
        : props.kind === "input" ? "Focused"
        : "Clicked";
      const context = [];
      if (props.column) context.push(`${props.column} column`);
      if (props.row) context.push(`${props.row} row`);
      if (props.kind === "column") context.push("column heading");
      if (props.kind === "heading") context.push("heading");
      if (props.kind === "section") context.push("section");
      const dialogLocation = props.dialog
        ? `${props.dialog} dialog ${props.dialogPart || "body"}`
        : null;
      if (dialogLocation && !String(props.label || "").toLowerCase().includes(dialogLocation.toLowerCase())) context.push(dialogLocation);
      if (props.dialogSection && props.dialogSection !== props.label && props.dialogSection !== dialogLocation) context.push(props.dialogSection);
      if (Number.isFinite(Number(props.clickX)) && Number.isFinite(Number(props.clickY))) {
        const exact = `x ${Math.round(Number(props.clickX))}, y ${Math.round(Number(props.clickY))}`;
        const relative = Number.isFinite(Number(props.horizontalPercent)) && Number.isFinite(Number(props.verticalPercent))
          ? `${Math.round(Number(props.horizontalPercent))}% from left, ${Math.round(Number(props.verticalPercent))}% from top`
          : null;
        context.push(relative ? `${relative} (${exact})` : exact);
      }
      if (props.page) context.push(`on ${props.page}`);
      return {
        title: `${verb} "${props.label || "a control"}"`,
        detail: context.length ? context.join(" · ") : null,
        kind: props.kind === "toggle" ? "toggle" : "click",
      };
    }
    case "onboarding_started":
      return { title: "Started company onboarding", detail: "Opened the workspace setup", kind: "onboarding" };
    case "onboarding_credit_model_selected":
      return { title: `Selected ${props.productName || props.productId || "a credit model"}`, detail: "Company onboarding · Credit model", kind: "onboarding" };
    case "onboarding_step_completed":
      return { title: `Completed ${props.stepName || `onboarding step ${props.step || ""}`}`, detail: "Company onboarding", kind: "onboarding" };
    case "onboarding_completed":
      return { title: "Completed company onboarding", detail: props.productName ? `Primary model: ${props.productName}` : "Workspace profile saved", kind: "onboarding" };
    case "onboarding_save_failed":
      return { title: "Onboarding save failed", detail: "The user remained in setup to retry", kind: "onboarding" };
    case "carrier_invited":
      return { title: "Invited to Carrier & Surety", detail: props.role ? `Role: ${prettyEvent(props.role)}` : "Onboarding invitation created", kind: "carrier" };
    case "carrier_link_sent":
      return { title: "Onboarding link sent", detail: "Carrier & Surety invitation email", kind: "link" };
    case "carrier_onboarding_link_opened":
      return { title: "Onboarding link opened", detail: `${props.openCount || 1} recorded open${Number(props.openCount) === 1 ? "" : "s"}`, kind: "link" };
    case "carrier_onboarding_started":
      return { title: "Started surety onboarding", detail: `${Number(props.completionPercent) || 0}% complete${props.section != null ? ` · section ${props.section} of 34` : ""}`, kind: "onboarding" };
    case "carrier_onboarding_progress":
      return { title: "Saved surety onboarding progress", detail: `${Number(props.completionPercent) || 0}% complete${props.section != null ? ` · section ${props.section} of 34` : ""}`, kind: "onboarding" };
    case "carrier_onboarding_submitted":
      return { title: "Submitted surety onboarding", detail: "Ready for administrator review and activation", kind: "onboarding" };
    case "carrier_activated":
      return { title: "Carrier access activated", detail: props.role ? `Role: ${prettyEvent(props.role)}` : "Full platform access granted", kind: "activation" };
    case "carrier_activation_link_opened":
      return { title: "Activation link opened", detail: `${props.openCount || 1} recorded open${Number(props.openCount) === 1 ? "" : "s"}`, kind: "link" };
    case "carrier_signed_in":
      return { title: "Signed in to the carrier platform", detail: "management.crediarc.net", kind: "login" };
    default:
      return { title: prettyEvent(ev.event), detail: null, kind: "page" };
  }
};

const pageKind = (p) => {
  const s = String(p || "");
  if (s.startsWith("/company")) return "company";
  if (s.startsWith("/settings")) return "settings";
  if (s.startsWith("/analytics") || s.startsWith("/reporting") || s.includes("/portfolio-risk")) return "insights";
  if (!s || s === "/") return "dashboard";
  return "page";
};

const KindIcon = ({ kind }) => {
  const c = kind === "refresh" || kind === "add" || kind === "activation" ? GREEN : kind === "login" ? "#7A5AF8" : kind === "click" || kind === "toggle" ? "#64748b" : BLUE;
  const I = kind === "login" ? LoginIcon
    : kind === "refresh" ? RefreshIcon
    : kind === "add" ? AddBusinessIcon
    : kind === "company" ? ApartmentIcon
    : kind === "settings" ? SettingsIcon
    : kind === "insights" ? InsightsIcon
    : kind === "dashboard" ? DashboardIcon
    : kind === "toggle" ? ToggleOnIcon
    : kind === "click" ? TouchAppIcon
    : kind === "link" ? LinkIcon
    : kind === "carrier" || kind === "activation" ? ShieldOutlinedIcon
    : DescriptionIcon;
  return (
    <Box sx={{ width: 30, height: 30, borderRadius: "9px", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", background: `${c}14`, border: `1px solid ${c}33` }}>
      <I sx={{ fontSize: 17, color: c }} />
    </Box>
  );
};

const ClickPositionMap = ({ event }) => {
  const props = event?.props || {};
  const left = Number(props.horizontalPercent);
  const top = Number(props.verticalPercent);
  if (!Number.isFinite(left) || !Number.isFinite(top)) return null;
  const safeLeft = Math.max(1, Math.min(99, left));
  const safeTop = Math.max(2, Math.min(98, top));
  return (
    <Box data-track-area="click position preview" sx={{ mt: 0.85, display: "flex", alignItems: "center", gap: 1 }}>
      <Box aria-label={`Click position ${Math.round(left)}% from left and ${Math.round(top)}% from top`}
        sx={{ position: "relative", width: 118, height: 66, flexShrink: 0, borderRadius: "7px", border: `1px solid ${LINE}`, bgcolor: "#f8fafc", overflow: "hidden",
          backgroundImage: "linear-gradient(#e8eef6 1px, transparent 1px), linear-gradient(90deg, #e8eef6 1px, transparent 1px)", backgroundSize: "25% 25%" }}>
        <Box sx={{ position: "absolute", top: 0, left: 0, right: 0, height: 8, bgcolor: "#dce5f0" }} />
        <Box sx={{ position: "absolute", left: `${safeLeft}%`, top: `${safeTop}%`, width: 10, height: 10, borderRadius: "50%", bgcolor: "#ef4444", border: "2px solid #fff", boxShadow: "0 0 0 2px rgba(239,68,68,.28)", transform: "translate(-50%, -50%)" }} />
      </Box>
      <Typography sx={{ fontSize: 10.5, lineHeight: 1.4, color: MUTED }}>
        {Math.round(left)}% across · {Math.round(top)}% down
        {Number.isFinite(Number(props.clickX)) && Number.isFinite(Number(props.clickY)) && <><br />x {Math.round(Number(props.clickX))}, y {Math.round(Number(props.clickY))}</>}
      </Typography>
    </Box>
  );
};

const FILTERS = [
  ["all", "All activity"],
  ["clicks", "Clicks"],
  ["pages", "Page views"],
  ["issues", "Possible issues"],
];

const groupMatchesFilter = (group, filter) => {
  if (filter === "all") return true;
  if (filter === "clicks") return group.event?.event === "click";
  if (filter === "pages") return group.event?.event === "page_view" || group.event?.event === "company_view";
  if (filter === "issues") return isFrustrationGroup(group) || /failed|error/i.test(String(group.event?.event || ""));
  return true;
};

export default function JourneyDrawer({ open, user, carrierJourney, onClose }) {
  const userId = user?.userId || user?.id || null;
  const [loading, setLoading] = useState(false);
  const [journey, setJourney] = useState([]);
  const [expanded, setExpanded] = useState(() => new Set());       // expanded session ids
  const [play, setPlay] = useState({ sid: null, cursor: -1 });     // per-session playback (chronological index)
  const [activityFilter, setActivityFilter] = useState("all");
  const itemRefs = useRef({});                                     // `${sid}:${chronoIdx}` -> el

  // Load + reset whenever a new user is opened.
  useEffect(() => {
    let cancelled = false;
    setExpanded(new Set()); setPlay({ sid: null, cursor: -1 }); setActivityFilter("all");
    if (!open || !userId) { setJourney([]); return; }
    setLoading(true);
    getAnalyticsUserJourney(userId, { limit: 800 })
      .then((j) => { if (!cancelled) setJourney(Array.isArray(j) ? j : []); })
      .catch(() => { if (!cancelled) setJourney([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, userId]);

  const combinedJourney = useMemo(
    () => [...journey, ...(Array.isArray(carrierJourney?.events) ? carrierJourney.events : [])],
    [journey, carrierJourney]
  );

  // Group events into sessions ("journeys"), newest session first, keep the last 10.
  const sessions = useMemo(() => {
    const map = new Map();
    for (const ev of combinedJourney) {
      const sid = ev.sessionId || `s-${ev.ts}`;
      if (!map.has(sid)) map.set(sid, []);
      map.get(sid).push(ev);
    }
    const arr = [...map.entries()].map(([sid, evs]) => {
      const chrono = [...evs].sort((a, b) => eventTime(a) - eventTime(b));
      return { sid, chrono, start: eventTime(chrono[0]), end: eventTime(chrono[chrono.length - 1]) };
    });
    arr.sort((a, b) => b.end - a.end); // most recent journey first
    return selectVisibleSessions(arr, 10); // always retain onboarding, even when older
  }, [combinedJourney]);

  const totalSessions = useMemo(() => new Set(combinedJourney.map((e) => e.sessionId).filter(Boolean)).size, [combinedJourney]);
  const sessionViews = useMemo(() => sessions.map((session) => {
    const groups = groupRepeatedEvents(session.chrono);
    const visibleGroups = groups.filter((group) => groupMatchesFilter(group, activityFilter));
    return {
      ...session,
      groups,
      visibleGroups,
      pages: sessionPages(session.chrono).size,
      frustrationCount: groups.filter(isFrustrationGroup).length,
    };
  }).filter((session) => session.visibleGroups.length > 0), [sessions, activityFilter]);
  const totalFrustration = useMemo(
    () => sessions.reduce((count, session) => count + groupRepeatedEvents(session.chrono).filter(isFrustrationGroup).length, 0),
    [sessions]
  );
  const topSid = sessions[0]?.sid;

  // Expand the most recent session by default once loaded, so there's content visible.
  useEffect(() => {
    if (topSid) setExpanded(new Set([topSid]));
  }, [topSid]);

  const toggle = (sid) => {
    setExpanded((prev) => { const n = new Set(prev); n.has(sid) ? n.delete(sid) : n.add(sid); return n; });
    setPlay({ sid: null, cursor: -1 });
  };

  // Playback: advance the chronological cursor within the playing session.
  useEffect(() => {
    if (play.sid == null) return;
    const s = sessions.find((x) => x.sid === play.sid);
    if (!s || play.cursor >= s.chrono.length - 1) return;
    const t = setTimeout(() => setPlay((p) => ({ ...p, cursor: p.cursor + 1 })), 1050);
    return () => clearTimeout(t);
  }, [play, sessions]);

  useEffect(() => {
    if (play.sid != null && play.cursor >= 0) {
      const el = itemRefs.current[`${play.sid}:${play.cursor}`];
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [play]);

  const startPlay = (e, sid) => {
    e.stopPropagation();
    setExpanded((prev) => new Set(prev).add(sid));
    setPlay({ sid, cursor: 0 });
  };
  const stopPlay = (e) => { e.stopPropagation(); setPlay({ sid: null, cursor: -1 }); };

  const title = user?.email || user?.name || shortId(userId);
  const metaParts = [];
  if (totalSessions) metaParts.push(`${fmt(totalSessions)} session${totalSessions === 1 ? "" : "s"}`);
  metaParts.push(`${fmt(combinedJourney.length)} step${combinedJourney.length === 1 ? "" : "s"}`);
  if (user?.tenantId) metaParts.push(`tenant ${shortId(user.tenantId)}`);

  return (
    <Drawer anchor="right" open={!!open} onClose={onClose}
      BackdropProps={{ sx: { backgroundColor: "rgba(15, 23, 42, .16)" } }}
      PaperProps={{ "aria-label": "User activity journey", "data-track-area": "user activity drawer", sx: { width: { xs: "100%", sm: 600, lg: 640 }, p: 0, display: "flex", flexDirection: "column", boxShadow: "-12px 0 36px rgba(15,35,60,.16)" } }}>
      {/* header */}
      <Box data-track-area="user activity drawer header" sx={{ p: 2.5, borderBottom: `1px solid ${LINE}`, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 16, fontWeight: 800, color: INK, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{title}</Typography>
          <Typography sx={{ fontSize: 12.5, color: MUTED }}>
            {metaParts.join(" · ")}{totalSessions > 10 ? " · showing last 10 + milestones" : ""}
          </Typography>
        </Box>
        <IconButton aria-label="Close user activity drawer" onClick={onClose}><CloseIcon /></IconButton>
      </Box>

      <Box data-track-area="user activity filters" sx={{ px: 2.5, py: 1.35, borderBottom: `1px solid ${LINE}`, display: "flex", gap: 0.75, alignItems: "center", flexWrap: "wrap", bgcolor: "#fbfcfe" }}>
        {FILTERS.map(([value, label]) => (
          <Chip key={value} label={label} size="small" clickable onClick={() => setActivityFilter(value)}
            aria-pressed={activityFilter === value}
            sx={{ height: 27, fontSize: 11.5, fontWeight: 700, color: activityFilter === value ? "#fff" : MUTED, bgcolor: activityFilter === value ? BLUE : "#eef2f7", "&:hover": { bgcolor: activityFilter === value ? "#165fd0" : "#e2e8f0" } }} />
        ))}
        {totalFrustration > 0 && <Chip size="small" icon={<WarningAmberRoundedIcon />} label={`${totalFrustration} repeated-click signal${totalFrustration === 1 ? "" : "s"}`} sx={{ ml: { sm: "auto" }, height: 27, fontSize: 11, fontWeight: 800, color: "#9a5b00", bgcolor: "#fff7df", "& .MuiChip-icon": { color: "#d97706" } }} />}
      </Box>

      {/* journeys — newest session at top, collapsed; click a journey to open its full steps */}
      <Box data-track-area="user journey activity list" sx={{ p: 2, overflowY: "auto", flex: 1 }}>
        {carrierJourney && (
          <Box data-track-area="Carrier and Surety stage panel" sx={{ border: `1px solid ${LINE}`, borderRadius: "12px", mb: 1.5, p: 1.75, background: "#f8fbff" }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mb: 1 }}>
              <Box>
                <Typography sx={{ fontSize: 13.5, color: INK, fontWeight: 800 }}>Carrier & Surety stage</Typography>
                <Typography sx={{ fontSize: 11.5, color: MUTED }}>{carrierJourney.company?.name}</Typography>
              </Box>
              <Chip size="small" label={carrierJourney.currentStage} sx={{ height: 22, fontSize: 11, fontWeight: 800, color: GREEN, bgcolor: "#dcfce7" }} />
            </Box>
            <LinearProgress variant="determinate" value={(carrierJourney.stages.filter((stage) => stage.complete).length / carrierJourney.stages.length) * 100} sx={{ height: 5, borderRadius: 3, mb: 1.25, bgcolor: "#e5edf7", "& .MuiLinearProgress-bar": { bgcolor: BLUE } }} />
            <Box sx={{ display: "grid", gap: 0.45 }}>
              {carrierJourney.stages.map((stage) => (
                <Box key={stage.key} sx={{ display: "grid", gridTemplateColumns: "14px minmax(0, 1fr) auto", gap: 0.75, alignItems: "center" }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: stage.state === "current" ? GREEN : stage.complete ? BLUE : "#dbe3ed", boxShadow: stage.state === "current" ? `0 0 0 3px ${GREEN}22` : "none" }} />
                  <Typography sx={{ fontSize: 12.5, color: stage.complete ? INK : "#94a3b8", fontWeight: stage.state === "current" ? 800 : 500 }}>
                    {stage.label}{stage.inferred ? " (inferred)" : ""}
                  </Typography>
                  <Typography sx={{ fontSize: 10.5, color: "#94a3b8", whiteSpace: "nowrap" }}>
                    {stage.timestamp ? fmtDayTime(new Date(stage.timestamp).getTime()) : "Pending"}
                  </Typography>
                </Box>
              ))}
            </Box>
            {carrierJourney.openCount > 0 && <Typography sx={{ fontSize: 11.5, color: MUTED, mt: 1 }}>{carrierJourney.openCount} tracked invitation-link open{carrierJourney.openCount === 1 ? "" : "s"}.</Typography>}
          </Box>
        )}
        {loading ? (
          <Box data-track-area="user journey loading area" sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress size={26} /></Box>
        ) : sessions.length ? (
          sessionViews.length ? sessionViews.map((s) => {
            const isOpen = expanded.has(s.sid);
            const isPlaying = play.sid === s.sid;
            const stepsNewestFirst = [...s.visibleGroups].reverse(); // last action at the top
            const isCarrierSession = String(s.sid).startsWith("carrier-surety-");
            const journeyLabel = isCarrierSession ? "Carrier and Surety journey" : `journey from ${fmtDayTime(s.end)}`;
            return (
              <Box key={s.sid} data-track-area={`${journeyLabel} card`} sx={{ border: `1px solid ${LINE}`, borderRadius: "12px", mb: 1.25, overflow: "hidden", background: "#fff" }}>
                {/* collapsed summary (click to expand) */}
                <Box role="button" tabIndex={0} aria-expanded={isOpen} data-track={isOpen ? `Collapse ${journeyLabel}` : `Expand ${journeyLabel}`}
                  onClick={() => toggle(s.sid)}
                  onKeyDown={(e) => { if (e.target === e.currentTarget && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); toggle(s.sid); } }}
                  sx={{ display: "flex", alignItems: "center", gap: 1, p: 1.5, cursor: "pointer", "&:hover": { background: "#f6f8fc" } }}>
                  <ExpandMoreIcon sx={{ fontSize: 20, color: MUTED, transform: isOpen ? "rotate(0deg)" : "rotate(-90deg)", transition: "transform .2s", flexShrink: 0 }} />
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: INK }}>{isCarrierSession ? "Carrier & Surety journey" : fmtDayTime(s.end)}</Typography>
                    <Typography sx={{ fontSize: 12, color: MUTED, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {s.chrono.length} step{s.chrono.length === 1 ? "" : "s"} · {fmtDuration(s.end - s.start)} · {s.pages} page{s.pages === 1 ? "" : "s"}
                    </Typography>
                  </Box>
                  {s.frustrationCount > 0 && <Tooltip title="Three or more equivalent clicks within five seconds"><Chip size="small" icon={<WarningAmberRoundedIcon />} label={s.frustrationCount} sx={{ height: 23, fontSize: 11, fontWeight: 800, color: "#9a5b00", bgcolor: "#fff7df", "& .MuiChip-icon": { color: "#d97706", fontSize: 15 } }} /></Tooltip>}
                  {isPlaying
                    ? <IconButton aria-label={`Pause ${journeyLabel}`} size="small" onClick={stopPlay}><PauseIcon fontSize="small" sx={{ color: BLUE }} /></IconButton>
                    : <Tooltip title="Play this journey"><IconButton aria-label={`Play ${journeyLabel}`} size="small" onClick={(e) => startPlay(e, s.sid)}><PlayArrowIcon fontSize="small" sx={{ color: BLUE }} /></IconButton></Tooltip>}
                </Box>

                {/* expanded full journey — steps newest-first */}
                {isOpen && (
                  <Box data-track-area={`${journeyLabel} steps`} sx={{ borderTop: `1px solid ${LINE}`, px: 1.5, py: 1 }}>
                    {activityFilter !== "all" && <Typography sx={{ px: 1, py: 0.5, fontSize: 11, color: MUTED }}>{s.visibleGroups.length} matching grouped event{s.visibleGroups.length === 1 ? "" : "s"}</Typography>}
                    {stepsNewestFirst.map((group, ri) => {
                      const ev = group.event;
                      const d = describeJourneyEvent(ev);
                      const active = isPlaying && group.indexes.includes(play.cursor);
                      const done = isPlaying && play.cursor > Math.max(...group.indexes);
                      const frustration = isFrustrationGroup(group);
                      return (
                        <Box key={`${group.signature}:${group.start}:${ri}`} ref={(el) => group.indexes.forEach((index) => { itemRefs.current[`${s.sid}:${index}`] = el; })} data-track-area={`journey step: ${d.title}`}
                          sx={{ display: "flex", gap: 1.25, alignItems: "flex-start", p: 1, borderRadius: "10px",
                            transition: "background .2s, opacity .2s",
                            opacity: isPlaying && !active && !done ? 0.45 : 1,
                            background: active ? "#eaf1fe" : frustration ? "#fffbeb" : "transparent",
                            boxShadow: active ? `inset 0 0 0 1px ${BLUE}55` : frustration ? "inset 0 0 0 1px #f59e0b44" : "none" }}>
                          <KindIcon kind={d.kind} />
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                              <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: INK }}>{d.title}</Typography>
                              {group.count > 1 && <Chip size="small" label={`×${group.count}`} sx={{ height: 20, fontSize: 10.5, fontWeight: 800, color: frustration ? "#9a5b00" : BLUE, bgcolor: frustration ? "#fff1c2" : "#eaf1fe" }} />}
                              {frustration && <Chip size="small" icon={<WarningAmberRoundedIcon />} label="Possible frustration" sx={{ height: 20, fontSize: 10, fontWeight: 800, color: "#9a5b00", bgcolor: "#fff1c2", "& .MuiChip-icon": { color: "#d97706", fontSize: 13 } }} />}
                            </Box>
                            {d.detail && <Typography sx={{ fontSize: 12, color: MUTED }}>{d.detail}</Typography>}
                            <ClickPositionMap event={ev} />
                          </Box>
                          <Typography sx={{ fontSize: 11.5, color: "#94a3b8", whiteSpace: "nowrap", pt: 0.25 }}>{group.count > 1 ? `${fmtClock(group.start)}–${fmtClock(group.end)}` : fmtClock(eventTime(ev))}</Typography>
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </Box>
            );
          }) : <Box data-track-area="no matching journey activity" sx={{ p: 3, textAlign: "center", border: `1px dashed ${LINE}`, borderRadius: "12px", color: MUTED }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700 }}>No activity matches this filter</Typography>
            <Typography sx={{ fontSize: 12, mt: 0.5 }}>Choose All activity to return to the complete journey.</Typography>
          </Box>
        ) : (
          <Typography data-track-area="empty user journey area" sx={{ fontSize: 13, color: MUTED, p: 1 }}>
            No activity recorded for this user yet. Journeys appear once the user is active in the app after analytics went live.
          </Typography>
        )}
      </Box>
    </Drawer>
  );
}
